import { useCallback } from 'react'
import { gql, useApolloClient } from '@apollo/client'
import produce from 'immer'

import {
  GetUserNotificationsCountDocument,
  GetUserNotificationsCountQuery,
  NotificationInfoFragment,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'

// `NotificationState` lives in gql-types' expansiveTypes and isn't re-exported
// from the package barrel, so derive the enum from the fragment field instead.
type NotificationState = NotificationInfoFragment['state']

// Concrete notification typenames (e.g. `CommentNotification`) — there's no
// `Notification` type; they all implement the `UserNotification` interface and
// are normalized in the cache under their concrete name.
type NotificationTypename = NotificationInfoFragment['__typename']

// The current fields are read from the cache before the mutation: `isPinned` is
// echoed back unchanged in the optimistic response (the mutation selects it),
// and the previous `state` tells us which way to nudge the unread count.
const NOTIFICATION_OPTIMISTIC_FIELDS = gql`
  fragment NotificationOptimisticFields on UserNotification {
    isPinned
    state
  }
`

/**
 * Returns a function that updates a notification's `state` via
 * `updateNotification`.
 *
 * The mutation now selects `state`, so Apollo normalizes the new value straight
 * onto the cached notification (clearing/restoring the unread dot in the list).
 * An `optimisticResponse` makes that happen before the request resolves — and
 * roll back if it fails. The unread-count badge reads from a separate aggregate
 * query, so it's nudged by hand for instant feedback and refetched afterwards to
 * reconcile with the server.
 */
export const useSetNotificationState = () => {
  const client = useApolloClient()
  const [updateNotification] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    refetchQueries: [{ query: GetUserNotificationsCountDocument }],
  })

  return useCallback(
    (
      identifier: string,
      typename: NotificationTypename,
      state: NotificationState
    ) => {
      const id = client.cache.identify({ __typename: typename, identifier })

      const current = id
        ? client.cache.readFragment<{
            isPinned: boolean
            state: NotificationState
          }>({ id, fragment: NOTIFICATION_OPTIMISTIC_FIELDS })
        : null

      const isPinned = current?.isPinned ?? false

      // +1 when the notification becomes unread, -1 when it leaves unread, 0 if
      // the unread-ness is unchanged.
      const unreadDelta =
        Number(state === 'UNREAD') - Number(current?.state === 'UNREAD')

      updateNotification({
        variables: { identifier, input: { state } },
        optimisticResponse: {
          __typename: 'RootMutationType',
          updateNotification: {
            __typename: 'NotificationUpdatedPayload',
            successful: true,
            notification: { __typename: typename, identifier, isPinned, state },
            errors: [],
          },
        },
        // `state` on the notification is normalized from the response above; only
        // the separate unread-count aggregate needs a manual nudge.
        update: cache => {
          if (unreadDelta === 0) return
          cache.updateQuery<GetUserNotificationsCountQuery>(
            { query: GetUserNotificationsCountDocument },
            data =>
              produce(data, draft => {
                const meta = draft?.me.unreadNotificationsCount?.meta
                if (meta) {
                  meta.totalCount = Math.max(0, meta.totalCount + unreadDelta)
                }
              })
          )
        },
      })
    },
    [client, updateNotification]
  )
}
